// Wesleyan University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Wesleyan's Common Data Set 2025-2026 (CDS_2025-2026.xlsx). The Office of
//    Institutional Research's Common Data Sets page links a SharePoint folder
//    (wesleyan0.sharepoint.com) that holds the Excel files, so the SOURCES
//    url is the wesleyan.edu page that links it. Read from the workbook's
//    answer sheet: C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table (it has an "unknown" residency row: 217 applied,
//    0 admitted), C8 test policy for Fall 2027, C21 early decision counts
//    for the Fall 2025 entering class (both rounds together). Its G block
//    marks final 2026-2027 costs as not yet available, so costs come from
//    the admission office's cost page.
//  - Wesleyan Admission (wesleyan.edu): First Year Applicants (dates,
//    application tabs, homeschool testing), Application Process (deadlines
//    table, the Early Decision Agreement), Admission FAQ (testing). The pages
//    carry no entry year; they are the pages as posted for the application
//    that opens in fall 2026.
//  - Wesleyan Admission, Cost of Attendance, 2026-2027 Academic Year.
//  - College Scorecard, school id 130697 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// wesleyan.edu refuses plain downloads, so every Wesleyan page was read in a
// browser. Cost: the page's first-year total ($101,080) includes the
// optional $50 Green Fund; the rows here leave it out and add to the page's
// own "Total without Green Fund Fee: $101,030 (First-Year)".
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Wesleyan Common Data Set 2025-2026',
        url: 'https://www.wesleyan.edu/ir/common-data-sets.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Wesleyan Admission, First Year Applicants',
        url: 'https://www.wesleyan.edu/admission/undergraduate-admission/first-year.html',
        read: '2026-10-01',
    },
    process: {
        label: 'Wesleyan Admission, Application Process',
        url: 'https://www.wesleyan.edu/admission/application-process.html',
        read: '2026-10-01',
    },
    faq: {
        label: 'Wesleyan Admission, FAQ',
        url: 'https://www.wesleyan.edu/admission/admission-faq.html',
        read: '2026-10-01',
    },
    coa: {
        label: 'Wesleyan Admission, Cost of Attendance',
        url: 'https://www.wesleyan.edu/admission/affordability-and-aid/cost-of-attendance.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?130697-Wesleyan-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'wesleyan-university',
    kbId: 'wesleyan_university',
    name: 'Wesleyan University',
    shortName: 'Wesleyan',
    aliases: ['Wes'],
    location: 'Middletown, CT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Wesleyan visual style guide: Cardinal Red, Black Squirrel); decoration only.
    branding: {
        primary: '#D72121',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/wesleyan_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 14973,
        admitted: 2451,
        enrolled: 825,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 935, admitted: 121 },
            { group: 'Out-of-state', applied: 8719, admitted: 2081 },
            { group: 'International', applied: 5102, admitted: 249 },
            { group: 'Residency unknown', applied: 217, admitted: 0 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Wesleyan\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by department or major. You apply to Wesleyan, not to a major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II', applied: 1220, admitted: 522 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them, with both rounds together. They are part of the totals above. Wesleyan has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every first-year applicant sends the same application, whatever they plan to study. Two groups can or must send more:',
        rows: [
            { school: 'Arts', requirement: 'Optional: you may send supporting work in music, theater, visual arts, film or dance through SlideRoom, which opens from the Common App.' },
            { school: 'Homeschooled applicants', requirement: 'Required: SAT or ACT scores from the last twelve months, a homeschool transcript, course descriptions, a program profile from the parent or educator, and letters from people who can assess your academic work.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March', binding: false },
        ],
        note: 'Financial aid applications are due November 15 for Early Decision I, January 1 for Early Decision II and January 15 for Regular Decision. The application fee is $65, waived for every applicant who applies for financial aid.',
        source: 'process',
        binding: 'Early Decision is binding. Your signed Early Decision Agreement, also signed by a parent or guardian, is due by the deadline: it says Wesleyan is your first choice, that you will accept an offer of admission, withdraw your other applications and start no new ones. If you want to apply to more than one college, apply Regular Decision. International citizens may apply Early Decision only if they are not applying for financial aid.',
        bindingSource: 'process',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Wesleyan has been test-optional since applicants for 2015 entry. You decide whether your SAT or ACT scores are considered: if they are in your application they are considered, and if not, they are not factored in and do not count against you. There is no minimum score. If you send scores, Wesleyan uses your best section scores across test dates, for both the SAT and the ACT.',
        source: 'faq',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75916 },
            { item: 'Student activity fee', amount: 404 },
            { item: 'New student matriculation fee', amount: 300 },
            { item: 'Food and housing', amount: 21660 },
            { item: 'Books, supplies and personal expenses', amount: 2750 },
        ],
        total: 101030,
        note: 'For a first-year student; every Wesleyan undergraduate lives in University housing on a University meal plan. Not included: the optional $50 Green Fund fee, a $44 loan fee if you take federal Direct Loans, and travel, which Wesleyan counts in your aid budget but does not list.',
        source: 'coa',
        netPrice: {
            average: 23014,
            byIncome: [
                { income: '$0 to $30,000', amount: 3557 },
                { income: '$30,001 to $48,000', amount: 3746 },
                { income: '$48,001 to $75,000', amount: 8768 },
                { income: '$75,001 to $110,000', amount: 12753 },
                { income: 'Over $110,000', amount: 45770 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Wesleyan\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
